import gql from 'graphql-tag';
import {
  CrmSummarySchema,
  InstallableIntegrationSchema,
  IntegrationConfigurationOptionsSchema,
  IntegrationSchema,
} from '../../definitions/schemas';
import {
  IntegrationType,
  IntegrationOwnerType,
} from '../../enums/Integrations/IntegrationType';
import Interface from '../Interface';

interface DeleteIntegrationInput {
  id: string;
}
interface GetIntegrationsData {
  integrations?: IntegrationSchema[];
}
interface InstallableIntegrationsData {
  installableIntegrations: InstallableIntegrationSchema[];
}

interface ConfigurationOptionsData {
  integrationConfigurationOptions: {
    configuration: IntegrationConfigurationOptionsSchema;
  };
}

interface UpdateIntegrationInput {
  id: string;
  configuration:
    | {
        notifyRoomCreation: boolean;
        notifyRoomEdit: boolean;
        notifyRoomVisit: boolean;
        provisionContactWhenNotifying: boolean;
      }
    | {
        notifications: Array<{
          roomNotificationChannelId: string | null;
          sendDirectMessage: boolean;
        }>;
      };
}
interface SearchInput {
  integrationType?: IntegrationType;
  ownerType?: IntegrationOwnerType;
}

interface GetCrmSummaryData {
  crmConnectionSummary: CrmSummarySchema;
}

export default class IntegrationsInterface extends Interface {
  public async installableIntegrations() {
    const response = await this.query<InstallableIntegrationsData>(
      () =>
        this.apolloService.executeQuery<InstallableIntegrationsData>({
          query: gql`
            query installableIntegrations {
              installableIntegrations {
                providerName
                integrationType
                installUrl
              }
            }
          `,
        }),
      'Error pulling integrations'
    );
    return response.installableIntegrations;
  }

  public async configurationOptions(id: string) {
    const response = await this.query<ConfigurationOptionsData>(
      () =>
        this.apolloService.executeQuery<ConfigurationOptionsData>({
          query: gql`
            query integrationConfigurationOptions($id: ID!) {
              integrationConfigurationOptions(id: $id) {
                configuration
              }
            }
          `,
          variables: { id },
        }),
      'Error pulling integrations'
    );
    return response.integrationConfigurationOptions.configuration;
  }

  public async search(variables?: SearchInput) {
    const response = await this.query<GetIntegrationsData>(
      () =>
        this.apolloService.executeQuery<GetIntegrationsData>({
          query: gql`
            query integrations(
              $integrationType: [IntegrationTypeEnum!]
              $ownerType: IntegrationOwnerTypeEnum
            ) {
              integrations(
                integrationType: $integrationType
                ownerType: $ownerType
              ) {
                id
                configuration
                connectionName
                deactivated
                integrationType
                metadata
                providerName
                version
              }
            }
          `,
          variables,
        }),
      'Error pulling integrations'
    );
    return response.integrations;
  }

  public async uninstall(input: DeleteIntegrationInput) {
    return this.mutate<null>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation deleteIntegration($input: DeleteIntegrationInput!) {
            deleteIntegration(input: $input) {
              errors
              message
            }
          }
        `,
        variables: {
          input,
        },
      });

      const { errors, message } = response.data.deleteIntegration;

      return {
        data: null,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error uninstalling integration');
  }

  public async update(input: UpdateIntegrationInput) {
    return this.mutate<UpdateIntegrationInput>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateIntegration($input: UpdateIntegrationInput!) {
            updateIntegration(input: $input) {
              errors
              message
              integration {
                id
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      const { errors, message } = response.data.updateIntegration;

      return {
        data: response.data,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error updating crm integration');
  }

  public async crmSummary() {
    const response = await this.query<GetCrmSummaryData>(
      () =>
        this.apolloService.executeQuery<GetCrmSummaryData>({
          query: gql`
            query crmConnectionSummary {
              crmConnectionSummary {
                companiesSyncedTotal
                contactsSyncedTotal
                createdAt
                dealsSyncedTotal
                dealStagesSyncedTotal
                dealTypesSyncedTotal
                lastActivityAt
                usersSyncedTotal
              }
            }
          `,
        }),
      'Error pulling crm summary'
    );
    return response.crmConnectionSummary;
  }
}
